import React, { useState, useEffect, useMemo } from 'react';
import {
  UserGroupIcon,
  ShieldCheckIcon,
  KeyIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  EyeIcon,
  CogIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../components/common/LoadingSpinner';

const UnifiedRoleManagement = () => {
  const [roles, setRoles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedView, setSelectedView] = useState('roles'); // roles, assignments, permissions, hierarchy
  const [selectedRole, setSelectedRole] = useState('');
  const [rolePermissions, setRolePermissions] = useState({});
  const [expandedModules, setExpandedModules] = useState(new Set());
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [selectedEmployeeForAssignment, setSelectedEmployeeForAssignment] = useState(null);
  const [syncingRoles, setSyncingRoles] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedRole && selectedView === 'permissions') {
      loadRolePermissions();
    }
  }, [selectedRole, selectedView]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('authToken');
      
      const [rolesResponse, employeesResponse, deptsResponse] = await Promise.all([
        fetch('/api/centralized-roles/hierarchy', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch('/api/centralized-roles/employees-with-roles', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch('/api/departments', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (!rolesResponse.ok || !employeesResponse.ok || !deptsResponse.ok) {
        throw new Error('Failed to load data from one or more APIs');
      }

      const [rolesData, employeesData, deptsData] = await Promise.all([
        rolesResponse.json(),
        employeesResponse.json(),
        deptsResponse.json()
      ]);

      if (rolesData.success) {
        // Enhance roles with user counts and permission summaries
        const enhancedRoles = await Promise.all(rolesData.data.map(async (role) => {
          // Get user count for this role
          const userCount = employeesData.data?.filter(emp => 
            emp.system_role === role.role_key || emp.role_name === role.role_name
          ).length || 0;

          return {
            ...role,
            user_count: userCount,
            role_source: role.role_source || 'organizational',
            is_system_role: role.is_management_role || role.is_executive_role || role.is_director_role
          };
        }));
        
        setRoles(enhancedRoles);
      }
      
      if (employeesData.success) {
        setEmployees(employeesData.data);
      }
      
      if (deptsData.success || deptsData.departments) {
        setDepartments(deptsData.departments || deptsData.data?.departments || deptsData.data);
      }

    } catch (err) {
      console.error('Error loading unified role data:', err);
      setError('Failed to load role management data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadRolePermissions = async () => {
    if (!selectedRole) return;
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/menu-permissions/roles/${selectedRole}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to load permissions: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Convert to easier format for UI
      const permissionsByModule = {};
      data.modulePermissions.forEach(module => {
        permissionsByModule[module.module] = {
          ...module,
          permissions: module.permissions.reduce((acc, perm) => {
            acc[perm.action] = {
              ...perm,
              granted: perm.granted
            };
            return acc;
          }, {})
        };
      });
      
      setRolePermissions(permissionsByModule);
    } catch (error) {
      console.error('Error loading role permissions:', error);
      setMessage('Failed to load role permissions: ' + error.message);
    }
  };

  const syncRoles = async () => {
    setSyncingRoles(true);
    setMessage('');
    try {
      const token = localStorage.getItem('authToken');
      
      // Sync centralized roles first
      const syncResponse = await fetch('/api/centralized-roles/synchronize', {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Sync menu permissions
      const permSyncResponse = await fetch('/api/menu-permissions/sync', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (syncResponse.ok && permSyncResponse.ok) {
        setMessage('✅ Role synchronization completed successfully');
        await loadData(); // Reload all data
      } else {
        throw new Error('Failed to sync roles and permissions');
      }
    } catch (error) {
      console.error('Error syncing roles:', error);
      setMessage('❌ Failed to sync roles: ' + error.message);
    } finally {
      setSyncingRoles(false);
    }
  };

  const handleSaveRole = async (roleData) => {
    try {
      const token = localStorage.getItem('authToken');
      
      const url = editingRole 
        ? `/api/organizational-roles/definitions/${editingRole.id}`
        : '/api/organizational-roles/definitions';
      const method = editingRole ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(roleData)
      });
      
      const result = await response.json();
      
      if (result.success) {
        setMessage(`✅ Role ${editingRole ? 'updated' : 'created'} successfully`);
        await syncRoles(); // Sync after role changes
        setShowRoleModal(false);
        setEditingRole(null);
      } else {
        throw new Error(result.message || 'Failed to save role');
      }
    } catch (error) {
      setMessage('❌ Error saving role: ' + error.message);
    }
  };

  const handleDeleteRole = async (roleId, roleName) => {
    if (!window.confirm(`Are you sure you want to delete the role "${roleName}"? This will affect all users with this role.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/organizational-roles/definitions/${roleId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        setMessage(`✅ Role "${roleName}" deleted successfully`);
        await syncRoles(); // Sync after role deletion
      } else {
        throw new Error(result.message || 'Failed to delete role');
      }
    } catch (error) {
      setMessage('❌ Error deleting role: ' + error.message);
    }
  };

  const updatePermission = (module, action, granted) => {
    setRolePermissions(prev => ({
      ...prev,
      [module]: {
        ...prev[module],
        permissions: {
          ...prev[module]?.permissions,
          [action]: {
            ...prev[module]?.permissions?.[action],
            granted
          }
        }
      }
    }));
  };

  const savePermissions = async () => {
    if (!selectedRole) return;
    
    try {
      setLoading(true);
      setMessage('');
      
      // Convert back to API format
      const permissionsToUpdate = [];
      Object.values(rolePermissions).forEach(module => {
        if (module.permissions) {
          Object.values(module.permissions).forEach(permission => {
            permissionsToUpdate.push({
              permission_id: permission.id,
              granted: permission.granted
            });
          });
        }
      });
      
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/menu-permissions/roles/${selectedRole}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ permissions: permissionsToUpdate })
      });
      
      if (response.ok) {
        setMessage(`✅ Permissions updated successfully for ${selectedRole} role`);
        await syncRoles(); // Sync after permission changes
      } else {
        throw new Error('Failed to update permissions');
      }
    } catch (error) {
      console.error('Error saving permissions:', error);
      setMessage('❌ Failed to save permissions: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleModule = (moduleKey) => {
    const newExpanded = new Set(expandedModules);
    if (newExpanded.has(moduleKey)) {
      newExpanded.delete(moduleKey);
    } else {
      newExpanded.add(moduleKey);
    }
    setExpandedModules(newExpanded);
  };

  const getModulePermissionsSummary = (moduleKey) => {
    const moduleData = rolePermissions[moduleKey];
    if (!moduleData?.permissions) return { granted: 0, total: 0 };
    
    const permissions = Object.values(moduleData.permissions);
    const granted = permissions.filter(p => p.granted).length;
    return { granted, total: permissions.length };
  };

  if (loading && roles.length === 0) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <UserGroupIcon className="w-8 h-8 text-blue-600 mr-3" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Unified Role Management</h1>
              <p className="text-gray-600">Manage roles, hierarchy, permissions, and assignments in one place</p>
            </div>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowRoleModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center space-x-2"
            >
              <PlusIcon className="h-4 w-4" />
              <span>Add Role</span>
            </button>
            <button
              onClick={syncRoles}
              disabled={syncingRoles}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center space-x-2"
            >
              <ArrowPathIcon className={`h-4 w-4 ${syncingRoles ? 'animate-spin' : ''}`} />
              <span>{syncingRoles ? 'Syncing...' : 'Sync All'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-md ${
          message.includes('✅') 
            ? 'bg-green-100 border border-green-400 text-green-700' 
            : 'bg-red-100 border border-red-400 text-red-700'
        }`}>
          {message}
        </div>
      )}

      {/* View Toggle */}
      <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
        {[
          { key: 'roles', label: 'Role Definitions', icon: UserGroupIcon },
          { key: 'permissions', label: 'Permissions', icon: ShieldCheckIcon },
          { key: 'assignments', label: 'Assignments', icon: KeyIcon },
          { key: 'hierarchy', label: 'Hierarchy View', icon: EyeIcon }
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setSelectedView(key)}
            className={`px-4 py-2 rounded-md text-sm font-medium flex items-center space-x-2 ${
              selectedView === key
                ? 'bg-white text-gray-900 shadow'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Icon className="w-4 h-4" />
            <span>{label}</span>
          </button>
        ))}
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {/* Content based on selected view */}
      {selectedView === 'roles' && (
        <RoleDefinitionsView 
          roles={roles} 
          onEditRole={setEditingRole}
          onDeleteRole={handleDeleteRole}
          onRefresh={loadData}
        />
      )}

      {selectedView === 'permissions' && (
        <PermissionsView
          roles={roles}
          selectedRole={selectedRole}
          setSelectedRole={setSelectedRole}
          rolePermissions={rolePermissions}
          expandedModules={expandedModules}
          toggleModule={toggleModule}
          updatePermission={updatePermission}
          savePermissions={savePermissions}
          getModulePermissionsSummary={getModulePermissionsSummary}
          loading={loading}
        />
      )}

      {selectedView === 'assignments' && (
        <AssignmentsView
          employees={employees}
          departments={departments}
          roles={roles}
          onRefresh={loadData}
          onShowAssignmentModal={(employee = null) => {
            setSelectedEmployeeForAssignment(employee);
            setShowAssignmentModal(true);
          }}
        />
      )}

      {selectedView === 'hierarchy' && (
        <HierarchyView 
          employees={employees}
          roles={roles}
          departments={departments}
        />
      )}

      {/* Modals */}
      {(showRoleModal || editingRole) && (
        <RoleModal
          role={editingRole}
          onClose={() => {
            setShowRoleModal(false);
            setEditingRole(null);
          }}
          onSave={handleSaveRole}
        />
      )}

      {showAssignmentModal && (
        <AssignmentModal
          employees={employees}
          roles={roles}
          selectedEmployee={selectedEmployeeForAssignment}
          onClose={() => {
            setShowAssignmentModal(false);
            setSelectedEmployeeForAssignment(null);
          }}
          onSave={loadData}
        />
      )}
    </div>
  );
};

// Role Definitions View Component
const RoleDefinitionsView = ({ roles, onEditRole, onDeleteRole, onRefresh }) => {
  const sortedRoles = [...roles].sort((a, b) => {
    // Sort by hierarchy level (desc), then by name
    if (b.hierarchy_level !== a.hierarchy_level) {
      return (b.hierarchy_level || 0) - (a.hierarchy_level || 0);
    }
    return a.role_name.localeCompare(b.role_name);
  });

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Unified Role Definitions</h3>
        <p className="text-sm text-gray-500">Manage roles that control both hierarchy and system access</p>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role Details
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Hierarchy & Users
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Capabilities
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Source & Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedRoles.map((role) => (
              <tr key={role.id || role.role_key} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{role.role_name}</div>
                    <div className="text-sm text-gray-500">{role.role_description || role.description}</div>
                    <div className="text-xs text-blue-600 mt-1">Key: {role.role_key}</div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="space-y-1">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Level {role.hierarchy_level || 1}
                    </span>
                    <div className="text-sm text-gray-600">
                      {role.user_count || 0} users assigned
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-wrap gap-1">
                    {role.can_approve_workflows && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        Workflows
                      </span>
                    )}
                    {role.can_approve_leave && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                        Leave
                      </span>
                    )}
                    {role.can_approve_expenses && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                        Expenses
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="space-y-1">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      role.role_source === 'system' 
                        ? 'bg-gray-100 text-gray-800' 
                        : 'bg-indigo-100 text-indigo-800'
                    }`}>
                      {role.role_source || 'organizational'}
                    </span>
                    <div className="flex flex-wrap gap-1">
                      {role.is_management_role && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                          Management
                        </span>
                      )}
                      {role.is_executive_role && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                          Executive
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => onEditRole(role)}
                      className="text-indigo-600 hover:text-indigo-900"
                      title="Edit Role"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    {role.role_source !== 'system' && (
                      <button
                        onClick={() => onDeleteRole(role.id, role.role_name)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete Role"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Permissions View Component
const PermissionsView = ({ 
  roles, selectedRole, setSelectedRole, rolePermissions, expandedModules, 
  toggleModule, updatePermission, savePermissions, getModulePermissionsSummary, loading 
}) => {
  return (
    <div className="space-y-6">
      {/* Role Selection */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <ShieldCheckIcon className="w-5 h-5 text-gray-600 mr-2" />
          Permission Management
        </h2>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="block w-64 border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select a role...</option>
              {roles.map(role => (
                <option key={role.role_key} value={role.role_key}>
                  {role.role_name} ({role.user_count || 0} users)
                </option>
              ))}
            </select>
            
            {selectedRole && (
              <button
                onClick={savePermissions}
                disabled={loading}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save Permissions'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Permissions Configuration */}
      {selectedRole && Object.keys(rolePermissions).length > 0 && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Permissions for {selectedRole} Role
          </h3>
          
          <div className="space-y-4">
            {Object.entries(rolePermissions).map(([moduleKey, moduleData]) => {
              const isExpanded = expandedModules.has(moduleKey);
              const summary = getModulePermissionsSummary(moduleKey);
              
              return (
                <div key={moduleKey} className="border border-gray-200 rounded-lg">
                  <div 
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                    onClick={() => toggleModule(moduleKey)}
                  >
                    <div className="flex items-center">
                      {isExpanded ? 
                        <ChevronDownIcon className="w-5 h-5 text-gray-400 mr-2" /> :
                        <ChevronRightIcon className="w-5 h-5 text-gray-400 mr-2" />
                      }
                      <h4 className="font-medium text-gray-900">{moduleData.moduleName}</h4>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-500">
                        {summary.granted}/{summary.total} permissions granted
                      </span>
                      <div className="w-16 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ width: `${summary.total > 0 ? (summary.granted / summary.total) * 100 : 0}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  
                  {isExpanded && moduleData.permissions && (
                    <div className="border-t border-gray-200 p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {Object.entries(moduleData.permissions).map(([action, permission]) => (
                          <label key={action} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={permission.granted}
                              onChange={(e) => updatePermission(moduleKey, action, e.target.checked)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3"
                            />
                            <span className="text-sm text-gray-700 capitalize">
                              {action} {moduleData.moduleName}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Import assignment and hierarchy components from the original file
const AssignmentsView = ({ employees, departments, roles, onRefresh, onShowAssignmentModal }) => {
  // Implementation similar to RoleAssignmentsView in original file
  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Role Assignments</h3>
      <p className="text-sm text-gray-500 mb-4">Assign roles to employees for both hierarchy and system access</p>
      {/* Assignment table implementation */}
      <div className="text-center py-8 text-gray-500">
        Assignment view implementation will be similar to OrganizationRoleManagement
      </div>
    </div>
  );
};

const HierarchyView = ({ employees, roles, departments }) => {
  const [viewMode, setViewMode] = useState('tree'); // 'tree' or 'list'
  const [selectedDepartment, setSelectedDepartment] = useState('');
  
  // Group employees by hierarchy level and department
  const hierarchyData = useMemo(() => {
    const filtered = employees.filter(emp => 
      !selectedDepartment || emp.department_name === selectedDepartment
    );
    
    return filtered.reduce((acc, employee) => {
      const level = employee.hierarchy_level || 1;
      const dept = employee.department_name || 'Unassigned';
      
      if (!acc[level]) acc[level] = {};
      if (!acc[level][dept]) acc[level][dept] = [];
      
      acc[level][dept].push(employee);
      return acc;
    }, {});
  }, [employees, selectedDepartment]);
  
  const maxLevel = Math.max(...Object.keys(hierarchyData).map(Number), 1);
  
  return (
    <div className="space-y-6">
      {/* Header with View Mode Toggle */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <EyeIcon className="w-5 h-5 text-gray-600 mr-2" />
              Organization Hierarchy
            </h2>
            <p className="text-sm text-gray-500">Visual representation of role-based hierarchy with system access levels</p>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Department Filter */}
            <select
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Departments</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.name}>{dept.name}</option>
              ))}
            </select>
            
            {/* View Mode Toggle */}
            <div className="flex border border-gray-300 rounded-md">
              <button
                onClick={() => setViewMode('tree')}
                className={`px-3 py-2 text-sm ${viewMode === 'tree' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
                } rounded-l-md`}
              >
                Tree View
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-2 text-sm ${viewMode === 'list' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
                } rounded-r-md border-l border-gray-300`}
              >
                List View
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Hierarchy Visualization */}
      <div className="bg-white shadow rounded-lg p-6">
        {viewMode === 'tree' ? (
          // Tree View
          <div className="space-y-8">
            {Array.from({ length: maxLevel }, (_, i) => i + 1).reverse().map(level => {
              const levelData = hierarchyData[level] || {};
              const hasEmployees = Object.values(levelData).some(empList => empList.length > 0);
              
              if (!hasEmployees) return null;
              
              return (
                <div key={level} className="relative">
                  <div className="flex items-center mb-4">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-medium">
                        {level}
                      </div>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-lg font-medium text-gray-900">Level {level}</h3>
                      <p className="text-sm text-gray-500">Hierarchy Level {level}</p>
                    </div>
                  </div>
                  
                  <div className="ml-11 space-y-4">
                    {Object.entries(levelData).map(([deptName, empList]) => (
                      <div key={deptName} className="border border-gray-200 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-3">{deptName}</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {empList.map(employee => (
                            <div key={employee.employee_id} className="flex items-center p-3 bg-gray-50 rounded-md">
                              <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                                  <span className="text-xs font-medium text-gray-600">
                                    {employee.first_name?.charAt(0)}{employee.last_name?.charAt(0)}
                                  </span>
                                </div>
                              </div>
                              <div className="ml-3 min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {employee.first_name} {employee.last_name}
                                </p>
                                <p className="text-xs text-gray-500 truncate">
                                  {employee.role_name || 'No Role'}
                                </p>
                                <p className="text-xs text-blue-600">
                                  {employee.system_role || 'employee'}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          // List View
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Employee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Department
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Organizational Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    System Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hierarchy Level
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Capabilities
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {employees
                  .filter(emp => !selectedDepartment || emp.department_name === selectedDepartment)
                  .sort((a, b) => (b.hierarchy_level || 1) - (a.hierarchy_level || 1))
                  .map(employee => {
                    const role = roles.find(r => r.role_key === employee.role_definition_id || r.role_name === employee.role_name);
                    return (
                      <tr key={employee.employee_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                <span className="text-sm font-medium text-gray-700">
                                  {employee.first_name?.charAt(0)}{employee.last_name?.charAt(0)}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {employee.first_name} {employee.last_name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {employee.employee_number}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {employee.department_name || 'Unassigned'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {employee.role_name ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {employee.role_name}
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              No Role
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {employee.system_role || 'employee'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-medium">
                              {employee.hierarchy_level || 1}
                            </div>
                            <span className="ml-2 text-sm text-gray-500">
                              Level {employee.hierarchy_level || 1}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="space-y-1">
                            {role?.can_approve_workflows && (
                              <div className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800 mr-1">
                                Workflow Approver
                              </div>
                            )}
                            {role?.can_approve_leave && (
                              <div className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 mr-1">
                                Leave Approver
                              </div>
                            )}
                            {role?.can_approve_expenses && (
                              <div className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800 mr-1">
                                Expense Approver
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                }
              </tbody>
            </table>
          </div>
        )}
        
        {employees.length === 0 && (
          <div className="text-center py-12">
            <EyeIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No hierarchy data</h3>
            <p className="mt-1 text-sm text-gray-500">
              Assign organizational roles to employees to see the hierarchy visualization.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Modal components
const RoleModal = ({ role, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    role_key: role?.role_key || '',
    role_name: role?.role_name || '',
    role_description: role?.role_description || role?.description || '',
    hierarchy_level: role?.hierarchy_level || 1,
    can_approve_workflows: role?.can_approve_workflows || false,
    can_approve_leave: role?.can_approve_leave || false,
    can_approve_expenses: role?.can_approve_expenses || false,
    can_approve_overtime: role?.can_approve_overtime || false,
    approval_limit_amount: role?.approval_limit_amount || '',
    is_management_role: role?.is_management_role || false,
    is_executive_role: role?.is_executive_role || false,
    is_director_role: role?.is_director_role || false,
    sort_order: role?.sort_order || 1
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Error saving role:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-screen overflow-y-auto">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {role ? 'Edit Unified Role' : 'Create New Unified Role'}
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <h4 className="text-sm font-medium text-blue-800 mb-2">Unified Role System</h4>
            <p className="text-sm text-blue-700">
              This role will control both organizational hierarchy (reporting, approvals) 
              and system access (navigation, permissions). Changes will be synchronized 
              across all modules automatically.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role Key * <span className="text-xs text-gray-500">(System identifier)</span>
              </label>
              <input
                type="text"
                name="role_key"
                value={formData.role_key}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., director, manager"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Display Name * <span className="text-xs text-gray-500">(User-friendly name)</span>
              </label>
              <input
                type="text"
                name="role_name"
                value={formData.role_name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Director, Manager"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description <span className="text-xs text-gray-500">(Role responsibilities)</span>
            </label>
            <textarea
              name="role_description"
              value={formData.role_description}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Describe role responsibilities and authority level"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hierarchy Level * <span className="text-xs text-gray-500">(1=lowest, 10=highest)</span>
              </label>
              <input
                type="number"
                name="hierarchy_level"
                value={formData.hierarchy_level}
                onChange={handleChange}
                min="1"
                max="10"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Approval Limit <span className="text-xs text-gray-500">(Amount in currency)</span>
              </label>
              <input
                type="number"
                name="approval_limit_amount"
                value={formData.approval_limit_amount}
                onChange={handleChange}
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Maximum approval amount"
              />
            </div>
          </div>
          
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700">Approval Capabilities</h4>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="can_approve_workflows"
                  checked={formData.can_approve_workflows}
                  onChange={handleChange}
                  className="mr-2"
                />
                Can Approve Workflows
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="can_approve_leave"
                  checked={formData.can_approve_leave}
                  onChange={handleChange}
                  className="mr-2"
                />
                Can Approve Leave
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="can_approve_expenses"
                  checked={formData.can_approve_expenses}
                  onChange={handleChange}
                  className="mr-2"
                />
                Can Approve Expenses
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="can_approve_overtime"
                  checked={formData.can_approve_overtime}
                  onChange={handleChange}
                  className="mr-2"
                />
                Can Approve Overtime
              </label>
            </div>
          </div>
          
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700">Role Classifications</h4>
            <div className="grid grid-cols-3 gap-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="is_management_role"
                  checked={formData.is_management_role}
                  onChange={handleChange}
                  className="mr-2"
                />
                Management Role
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="is_executive_role"
                  checked={formData.is_executive_role}
                  onChange={handleChange}
                  className="mr-2"
                />
                Executive Role
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="is_director_role"
                  checked={formData.is_director_role}
                  onChange={handleChange}
                  className="mr-2"
                />
                Director Role
              </label>
            </div>
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : (role ? 'Update Role' : 'Create Role')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AssignmentModal = ({ employees, roles, selectedEmployee, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    employee_id: selectedEmployee?.employee_id || '',
    role_definition_id: selectedEmployee?.role_definition_id || '',
    system_role: selectedEmployee?.system_role || 'employee',
    effective_from: new Date().toISOString().split('T')[0],
    is_primary_role: true,
    assignment_reason: 'Role assignment update'
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedEmployee) {
      setFormData({
        employee_id: selectedEmployee.employee_id || '',
        role_definition_id: selectedEmployee.role_definition_id || '',
        system_role: selectedEmployee.system_role || 'employee',
        effective_from: new Date().toISOString().split('T')[0],
        is_primary_role: true,
        assignment_reason: 'Role assignment update'
      });
    }
  }, [selectedEmployee]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.employee_id) {
      alert('Please select an employee');
      return;
    }

    setLoading(true);
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Error saving assignment:', error);
      alert('Failed to save assignment');
    } finally {
      setLoading(false);
    }
  };

  // If no employee selected, show employee selector
  const showEmployeeSelector = !selectedEmployee;
  const availableEmployees = showEmployeeSelector ? employees.filter(emp => 
    !roles.some(role => role.employee_id === emp.employee_id)
  ) : [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900">
            {selectedEmployee ? `Assign Role to ${selectedEmployee.first_name} ${selectedEmployee.last_name}` : 'Assign Role to Employee'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Employee Selection */}
          {showEmployeeSelector && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Employee
              </label>
              <select
                value={formData.employee_id}
                onChange={(e) => setFormData({...formData, employee_id: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Choose an employee...</option>
                {availableEmployees.map(employee => (
                  <option key={employee.employee_id} value={employee.employee_id}>
                    {employee.first_name} {employee.last_name} - {employee.employee_number}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Role Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Organizational Role
            </label>
            <select
              value={formData.role_definition_id}
              onChange={(e) => setFormData({...formData, role_definition_id: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="">Select organizational role...</option>
              {roles.map(role => (
                <option key={role.id} value={role.id}>
                  {role.role_name} (Level {role.hierarchy_level})
                  {role.can_approve_workflows && ' - Workflow Approver'}
                  {role.can_approve_leave && ' - Leave Approver'}
                  {role.can_approve_expenses && ' - Expense Approver'}
                </option>
              ))}
            </select>
          </div>

          {/* System Role Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              System Access Role
            </label>
            <select
              value={formData.system_role}
              onChange={(e) => setFormData({...formData, system_role: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="employee">Employee</option>
              <option value="supervisor">Supervisor</option>
              <option value="manager">Manager</option>
              <option value="hr_manager">HR Manager</option>
              <option value="admin">Administrator</option>
            </select>
            <p className="mt-1 text-sm text-gray-500">
              System role determines menu access and data permissions
            </p>
          </div>

          {/* Effective Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Effective From
            </label>
            <input
              type="date"
              value={formData.effective_from}
              onChange={(e) => setFormData({...formData, effective_from: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          {/* Assignment Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assignment Reason
            </label>
            <textarea
              value={formData.assignment_reason}
              onChange={(e) => setFormData({...formData, assignment_reason: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              rows={3}
              placeholder="Reason for this role assignment..."
            />
          </div>

          {/* Primary Role Checkbox */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_primary_role"
              checked={formData.is_primary_role}
              onChange={(e) => setFormData({...formData, is_primary_role: e.target.checked})}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3"
            />
            <label htmlFor="is_primary_role" className="text-sm text-gray-700">
              This is the employee's primary organizational role
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save Assignment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UnifiedRoleManagement;